import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { adminApi, apiClient } from '../../utils/api';

const initialCollection = () => ({ items: [], status: 'idle', error: null });

const initialState = {
    agents: initialCollection(),
    skills: initialCollection(),
    models: initialCollection(),
};

const shouldSkipFetch = (state) =>
    state.status === 'loading' || state.status === 'succeeded';

export const fetchDevAgents = createAsyncThunk(
    'devTools/fetchAgents',
    async (_, { rejectWithValue }) => {
        try {
            const data = await adminApi.listAgentOptions();
            return data?.items || [];
        } catch (err) {
            return rejectWithValue(err?.message || 'fetch failed');
        }
    },
    {
        condition: (_, { getState }) => !shouldSkipFetch(getState().devTools.agents),
    },
);

export const fetchDevSkills = createAsyncThunk(
    'devTools/fetchSkills',
    async (_, { rejectWithValue }) => {
        try {
            const data = await adminApi.listSkillOptions();
            return data?.items || [];
        } catch (err) {
            return rejectWithValue(err?.message || 'fetch failed');
        }
    },
    {
        condition: (_, { getState }) => !shouldSkipFetch(getState().devTools.skills),
    },
);

export const fetchDevModels = createAsyncThunk(
    'devTools/fetchModels',
    async (_, { rejectWithValue }) => {
        try {
            const data = await apiClient.getAvailableModels();
            return data?.models || [];
        } catch (err) {
            return rejectWithValue(err?.message || 'fetch failed');
        }
    },
    {
        condition: (_, { getState }) => !shouldSkipFetch(getState().devTools.models),
    },
);

const attachCollectionHandlers = (builder, thunk, key) => {
    builder
        .addCase(thunk.pending, (state) => {
            state[key].status = 'loading';
            state[key].error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
            state[key].status = 'succeeded';
            state[key].items = action.payload;
        })
        .addCase(thunk.rejected, (state, action) => {
            state[key].status = 'failed';
            state[key].error = action.payload || 'unknown';
        });
};

const devToolsSlice = createSlice({
    name: 'devTools',
    initialState,
    reducers: {
        devToolsReset() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        attachCollectionHandlers(builder, fetchDevAgents, 'agents');
        attachCollectionHandlers(builder, fetchDevSkills, 'skills');
        attachCollectionHandlers(builder, fetchDevModels, 'models');
    },
});

export const { devToolsReset } = devToolsSlice.actions;
export default devToolsSlice.reducer;

const selectRoot = (state) => state.devTools;
export const selectDevAgents = createSelector(selectRoot, (root) => root.agents.items);
export const selectDevAgentsStatus = createSelector(selectRoot, (root) => root.agents.status);
export const selectDevSkills = createSelector(selectRoot, (root) => root.skills.items);
export const selectDevSkillsStatus = createSelector(selectRoot, (root) => root.skills.status);
export const selectDevModels = createSelector(selectRoot, (root) => root.models.items);
export const selectDevModelsStatus = createSelector(selectRoot, (root) => root.models.status);
